import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  Loader2,
  Users,
  TrendingUp,
  CheckCircle2,
  AlertTriangle,
  Clock,
  Trophy,
  CircleDot,
  ArrowLeft,
  ShieldAlert,
  ChevronRight,
  Banknote,
} from "lucide-react";

import {
  crmDashboardStats,
  crmGetMyAccess,
  crmMetricDetails,
  getRenewalReminderCronHour,
  setRenewalReminderCronHour,
  type CrmMetricKey,
} from "@/lib/crm.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
const MyFollowUpsWidget = lazy(() =>
  import("@/components/crm/MyFollowUpsWidget").then((m) => ({ default: m.MyFollowUpsWidget })),
);



const BS = {
  primary: "#0d6efd",
  cardBorder: "#dee2e6",
  muted: "#6c757d",
  dark: "#212529",
};

export const Route = createFileRoute("/crm/dashboard")({
  component: CRMDashboardPage,
  errorComponent: ({ error }) => (
    <div className="grid min-h-screen place-items-center px-6 text-center">
      <p className="text-sm" style={{ color: BS.muted }}>
        {(error as Error | undefined)?.message ?? "Failed to load dashboard"}
      </p>
    </div>
  ),
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center">Not found</div>
  ),
});

function DashboardFallback() {
  return (
    <div className="space-y-4" aria-busy="true" aria-label="Loading dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {Array.from({ length: 8 }).map((_, i) => (
          <div key={i} className="h-[88px] rounded-xl border bg-white animate-pulse" style={{ borderColor: BS.cardBorder }} />
        ))}
      </div>
      <div className="h-40 rounded-xl border bg-white animate-pulse" style={{ borderColor: BS.cardBorder }} />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 h-64 rounded-xl border bg-white animate-pulse" style={{ borderColor: BS.cardBorder }} />
        <div className="h-64 rounded-xl border bg-white animate-pulse" style={{ borderColor: BS.cardBorder }} />
      </div>
    </div>
  );
}

function AccessDenied({ message }: { message: string }) {
  return (
    <div className="grid min-h-[40vh] place-items-center px-6 text-center">
      <div className="max-w-sm">
        <div
          className="mx-auto mb-3 grid h-12 w-12 place-items-center rounded-2xl"
          style={{ background: "rgba(220,53,69,0.12)", color: "#dc3545" }}
        >
          <ShieldAlert className="h-6 w-6" />
        </div>
        <h1 className="text-base font-bold" style={{ color: BS.dark }}>
          Access Denied
        </h1>
        <p className="mt-1 text-sm" style={{ color: BS.muted }}>
          {message}
        </p>
        <Link
          to="/crm"
          className="mt-4 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border bg-white hover:bg-[#f8f9fc]"
          style={{ borderColor: BS.cardBorder, color: BS.dark }}
        >
          <ArrowLeft className="size-3.5" /> Back to CRM
        </Link>
      </div>
    </div>
  );
}

function CRMDashboardPage() {
  const getMe = useServerFn(crmGetMyAccess);
  const getStats = useServerFn(crmDashboardStats);
  const getMetric = useServerFn(crmMetricDetails);
  const [openMetric, setOpenMetric] = useState<null | { key: CrmMetricKey; label: string; tint: string }>(null);

  const detailQ = useQuery({
    queryKey: ["crm", "metric-details", openMetric?.key],
    queryFn: () => getMetric({ data: { metric: openMetric!.key } }),
    enabled: !!openMetric,
  });



  const meQ = useQuery({
    queryKey: ["crm", "me"],
    queryFn: () => getMe(),
    staleTime: 5 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  const [rangeDays, setRangeDays] = useState<number>(0); // 0 = All time
  const rangePayload = (() => {
    if (rangeDays === 0) return {};
    const to = new Date();
    const from = new Date(to.getTime() - rangeDays * 24 * 3600 * 1000);
    return { from: from.toISOString(), to: to.toISOString() };
  })();

  const statsQ = useQuery({
    queryKey: ["crm", "dashboard-stats", rangeDays],
    queryFn: () => getStats({ data: rangePayload }),
    enabled: meQ.data?.has_crm_access === true,
    staleTime: 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  if (meQ.isLoading && !meQ.data) {
    return <DashboardFallback />;
  }
  if (meQ.isError || !meQ.data) {
    return <AccessDenied message="You must be signed in to view the CRM dashboard." />;
  }
  if (!meQ.data.has_crm_access) {
    return (
      <AccessDenied message="This dashboard is restricted to admins and sales reps. Ask an admin to grant you access." />
    );
  }

  if (statsQ.isError && !statsQ.data) {
    return (
      <div className="grid min-h-[40vh] place-items-center px-6 text-center">
        <p className="text-sm" style={{ color: "#dc3545" }}>
          {(statsQ.error as any)?.message ?? "Failed to load dashboard"}
        </p>
      </div>
    );
  }

  if (!statsQ.data) {
    return <DashboardFallback />;
  }


  const { totals, clients_per_day, contacts_per_day = [], by_segment, reps, is_admin } = statsQ.data as any;

  const contactsByDate = new Map<string, number>(
    (contacts_per_day as any[]).map((d) => [d.date, d.count]),
  );
  const maxDay = Math.max(
    1,
    ...clients_per_day.map((d: any) => d.count),
    ...(contacts_per_day as any[]).map((d) => d.count),
  );
  const contactsTotal = (contacts_per_day as any[]).reduce((s, d) => s + d.count, 0);

  // Outreach summary — clients contacted today / yesterday / last 7 / last 30 days
  const dayKey = (offset: number) => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() - offset);
    return d.toISOString().slice(0, 10);
  };
  const sumLastDays = (n: number) => {
    let total = 0;
    for (let i = 0; i < n; i++) total += contactsByDate.get(dayKey(i)) ?? 0;
    return total;
  };
  const outreach = [
    { label: "Today", value: contactsByDate.get(dayKey(0)) ?? 0 },
    { label: "Yesterday", value: contactsByDate.get(dayKey(1)) ?? 0 },
    { label: "Last 7 Days", value: sumLastDays(7) },
    { label: "Last 30 Days", value: sumLastDays(30) },
  ];
  const segmentTotal = Math.max(
    1,
    by_segment.eat_drink + by_segment.do + by_segment.happenings,
  );

  return (
    <div>
      <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: BS.muted }}>Range</span>
          {[
            { d: 0, l: "All" },
            { d: 7, l: "7d" },
            { d: 30, l: "30d" },
            { d: 90, l: "90d" },
            { d: 365, l: "12m" },
          ].map((r) => (
            <button
              key={r.d}
              onClick={() => setRangeDays(r.d)}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${rangeDays === r.d ? "bg-[#0d6efd] text-white border-[#0d6efd]" : "bg-white text-[#212529] hover:bg-[#f8f9fc]"}`}
              style={rangeDays === r.d ? undefined : { borderColor: BS.cardBorder }}
            >
              {r.l}
            </button>
          ))}
        </div>
        <span
          className="text-[10px] font-semibold uppercase tracking-wide px-2 py-1 rounded-full"
          style={{ background: "rgba(13,110,253,0.1)", color: BS.primary }}
        >
          {is_admin ? "Admin" : "My view"}
        </span>
      </div>
        {/* KPI grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-4 sm:mb-5">
          <Kpi icon={Users} label="Clients" value={totals.total_clients} tint="#0d6efd" onClick={() => setOpenMetric({ key: "total_clients", label: "All Clients", tint: "#0d6efd" })} />
          <Kpi icon={CheckCircle2} label="Active" value={totals.active_clients} tint="#198754" onClick={() => setOpenMetric({ key: "active_clients", label: "Active Clients", tint: "#198754" })} />
          <Kpi icon={CircleDot} label="Prospects" value={totals.prospects} tint="#f59e0b" onClick={() => setOpenMetric({ key: "prospects", label: "Prospects", tint: "#f59e0b" })} />
          <Kpi
            icon={TrendingUp}
            label="Pipeline €"
            value={`€${Math.round(totals.pipeline_value_eur).toLocaleString()}`}
            tint="#6f42c1"
            onClick={() => setOpenMetric({ key: "pipeline_value_eur", label: "Pipeline (Pending Deals)", tint: "#6f42c1" })}
          />
          <Kpi icon={Trophy} label="Won Deals" value={totals.won_deals} tint="#198754" onClick={() => setOpenMetric({ key: "won_deals", label: "Won Deals", tint: "#198754" })} />
          <Kpi
            icon={Banknote}
            label="Won Value €"
            value={`€${Math.round(totals.won_value_eur).toLocaleString()}`}
            tint="#0d6efd"
            onClick={() => setOpenMetric({ key: "won_value_eur", label: "Won Value Breakdown", tint: "#0d6efd" })}
          />
          <Kpi icon={Clock} label="Expiring 30d" value={totals.expiring_contracts} tint="#f59e0b" accent="yellow" onClick={() => setOpenMetric({ key: "expiring_contracts", label: "Contracts Expiring In 30 Days", tint: "#f59e0b" })} />
          <Kpi icon={AlertTriangle} label="Expired" value={totals.expired_contracts} tint="#dc3545" accent="red" onClick={() => setOpenMetric({ key: "expired_contracts", label: "Expired Contracts", tint: "#dc3545" })} />
        </div>

        <div className="mb-4 sm:mb-5">
          <Suspense fallback={<div className="h-40 rounded-xl border bg-white animate-pulse" style={{ borderColor: BS.cardBorder }} />}>
            <MyFollowUpsWidget />
          </Suspense>
        </div>

        {/* Outreach summary — how many clients were contacted in each period */}
        <div className="bg-white border rounded-2xl p-4 sm:p-5 mb-4 sm:mb-5 shadow-[0_1px_0_rgba(13,17,38,0.04)]" style={{ borderColor: BS.cardBorder }}>
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
                Outreach — Clients Contacted
              </h2>
              <p className="text-[11px] mt-0.5" style={{ color: BS.muted }}>
                Unique clients with a logged contact
              </p>
            </div>
            <Link
              to="/crm/reports/contacts"
              className="inline-flex items-center gap-1 text-[11px] font-medium hover:underline"
              style={{ color: BS.primary }}
            >
              Full contacts report <ChevronRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
            {outreach.map((o) => (
              <div
                key={o.label}
                className="rounded-xl border px-3 py-2.5"
                style={{ borderColor: BS.cardBorder, background: "#f8f9fc" }}
              >
                <div className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: BS.muted }}>
                  {o.label}
                </div>
                <div className="text-xl font-bold font-mono mt-0.5" style={{ color: "#198754" }}>
                  {o.value}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Charts row */}


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-5">
          {/* Clients added per day */}
          <div className="lg:col-span-2 bg-white border rounded-2xl p-4 sm:p-5 shadow-[0_1px_0_rgba(13,17,38,0.04)]" style={{ borderColor: BS.cardBorder }}>
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
                Clients Added & Contacted — Last 30 Days
              </h2>
              <div className="flex items-center gap-3 text-[11px] font-mono" style={{ color: BS.muted }}>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: BS.primary }} />
                  {clients_per_day.reduce((s: number, d: any) => s + d.count, 0)} added
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: "#198754" }} />
                  {contactsTotal} contacted
                </span>
              </div>
            </div>
            <div className="flex items-end gap-[3px] h-28 sm:h-40">
              {clients_per_day.map((d: any) => {
                const addedH = (d.count / maxDay) * 100;
                const contacted = contactsByDate.get(d.date) ?? 0;
                const contactedH = (contacted / maxDay) * 100;
                return (
                  <div key={d.date} className="flex-1 flex items-end gap-[1px] h-full" title={`${d.date}: ${d.count} added, ${contacted} contacted`}>
                    <div
                      className="flex-1 rounded-t transition-all hover:opacity-80"
                      style={{
                        height: `${Math.max(addedH, d.count ? 6 : 2)}%`,
                        background: d.count > 0 ? `linear-gradient(180deg, ${BS.primary} 0%, #4f8dff 100%)` : "#e9ecef",
                        minHeight: 2,
                      }}
                    />
                    <div
                      className="flex-1 rounded-t transition-all hover:opacity-80"
                      style={{
                        height: `${Math.max(contactedH, contacted ? 6 : 2)}%`,
                        background: contacted > 0 ? "linear-gradient(180deg, #198754 0%, #34c47a 100%)" : "#e9ecef",
                        minHeight: 2,
                      }}
                    />
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-[10px]" style={{ color: BS.muted }}>
              <span>{clients_per_day[0]?.date.slice(5)}</span>
              <span>{clients_per_day[clients_per_day.length - 1]?.date.slice(5)}</span>
            </div>
          </div>

          {/* Segment breakdown */}
          <div className="bg-white border rounded-2xl p-4 sm:p-5 shadow-[0_1px_0_rgba(13,17,38,0.04)]" style={{ borderColor: BS.cardBorder }}>
            <h2 className="text-sm font-bold mb-3" style={{ color: BS.dark }}>
              By Segment
            </h2>
            <div className="space-y-2.5">
              {[
                { key: "eat_drink", label: "Eat & Drink", color: "#0d6efd" },
                { key: "do", label: "Do", color: "#198754" },
                { key: "happenings", label: "Happenings", color: "#f59e0b" },
              ].map((s) => {
                const v = by_segment[s.key] || 0;
                const pct = Math.round((v / segmentTotal) * 100);
                return (
                  <div key={s.key}>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span style={{ color: BS.dark }}>{s.label}</span>
                      <span className="font-mono" style={{ color: BS.muted }}>
                        {v} · {pct}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ background: "#f1f3f5" }}>
                      <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, background: s.color }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-3 border-t grid grid-cols-3 gap-2 text-center" style={{ borderColor: BS.cardBorder }}>
              <MiniStat label="Pending" value={totals.pending_deals} color={BS.dark} />
              <MiniStat label="Lost" value={totals.lost_deals} color="#dc3545" />
              <MiniStat label="Churned" value={totals.churned} color={BS.dark} />
            </div>
          </div>
        </div>

        {/* Admin: per-rep activity */}
        {is_admin && (
          <div className="bg-white border rounded-2xl overflow-hidden shadow-[0_1px_0_rgba(13,17,38,0.04)]" style={{ borderColor: BS.cardBorder }}>
            <div className="px-4 sm:px-5 py-3 sm:py-4 border-b flex items-center justify-between" style={{ borderColor: BS.cardBorder }}>
              <div className="min-w-0">
                <h2 className="text-sm font-bold" style={{ color: BS.dark }}>
                  Salesperson Activity
                </h2>
                <p className="hidden sm:block text-[11px] mt-0.5" style={{ color: BS.muted }}>
                  Per-rep clients, won deals and pipeline.
                </p>
              </div>
              <span
                className="shrink-0 text-[11px] font-mono px-2 py-0.5 rounded-full"
                style={{ background: "rgba(13,110,253,0.1)", color: BS.primary }}
              >
                {reps.length} reps
              </span>
            </div>
            {reps.length === 0 ? (
              <div className="py-10 text-center text-sm" style={{ color: BS.muted }}>
                No salesperson activity yet.
              </div>
            ) : (
              <>
                {/* Mobile: cards */}
                <ul className="sm:hidden divide-y" style={{ borderColor: BS.cardBorder }}>
                  {reps.map((r: any) => {
                    const initial = (r.email ?? "?").charAt(0).toUpperCase();
                    return (
                      <li key={r.user_id} className="p-3">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-xs font-bold text-white"
                            style={{ background: "linear-gradient(135deg,#0d6efd,#4f8dff)" }}
                          >
                            {initial}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-sm font-semibold" style={{ color: BS.dark }}>
                              {r.email ?? r.user_id.slice(0, 8)}
                            </div>
                            <div className="text-[11px]" style={{ color: BS.muted }}>
                              €{Math.round(r.won_value_eur).toLocaleString()} won
                            </div>
                          </div>
                        </div>
                        <div className="mt-2.5 grid grid-cols-4 gap-1.5">
                          <RepPill label="Clients" value={r.clients} color={BS.dark} />
                          <RepPill label="Active" value={r.active} color="#198754" />
                          <RepPill label="Pending" value={r.pending} color="#f59e0b" />
                          <RepPill label="Won" value={r.won} color="#198754" />
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* Desktop: table */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left" style={{ background: "#f8f9fc" }}>
                        <th className="px-4 py-2.5 text-xs font-semibold" style={{ color: BS.muted }}>Salesperson</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-right" style={{ color: BS.muted }}>Clients</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-right" style={{ color: BS.muted }}>Active</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-right" style={{ color: BS.muted }}>Pending</th>
                        <th className="px-3 py-2.5 text-xs font-semibold text-right" style={{ color: BS.muted }}>Won</th>
                        <th className="px-4 py-2.5 text-xs font-semibold text-right" style={{ color: BS.muted }}>Won €</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reps.map((r: any) => {
                        const initial = (r.email ?? "?").charAt(0).toUpperCase();
                        return (
                          <tr key={r.user_id} className="border-t hover:bg-[#f8f9fc] transition-colors" style={{ borderColor: BS.cardBorder }}>
                            <td className="px-4 py-2.5">
                              <div className="flex items-center gap-2.5 min-w-0">
                                <div
                                  className="grid size-7 shrink-0 place-items-center rounded-full text-xs font-bold"
                                  style={{
                                    background: "linear-gradient(135deg, rgba(13,110,253,0.14), rgba(79,141,255,0.18))",
                                    color: BS.primary,
                                    border: "1px solid rgba(13,110,253,0.22)",
                                  }}
                                >
                                  {initial}
                                </div>
                                <span className="truncate" style={{ color: BS.dark }}>
                                  {r.email ?? r.user_id.slice(0, 8)}
                                </span>
                              </div>
                            </td>
                            <td className="px-3 py-2.5 text-right font-mono font-bold" style={{ color: BS.dark }}>{r.clients}</td>
                            <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#198754" }}>{r.active}</td>
                            <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#f59e0b" }}>{r.pending}</td>
                            <td className="px-3 py-2.5 text-right font-mono" style={{ color: "#198754" }}>{r.won}</td>
                            <td className="px-4 py-2.5 text-right font-mono" style={{ color: BS.dark }}>
                              €{Math.round(r.won_value_eur).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

      {meQ.data?.is_super_admin && <RenewalReminderCronCard />}

      <MetricDialog
        open={openMetric}
        loading={detailQ.isLoading}
        rows={detailQ.data?.rows ?? []}
        onClose={() => setOpenMetric(null)}
      />
    </div>
  );
}

function MetricDialog({
  open,
  loading,
  rows,
  onClose,
}: {
  open: null | { key: CrmMetricKey; label: string; tint: string };
  loading: boolean;
  rows: Array<{ id: string; client_id: string; primary: string; secondary: string; amount?: number }>;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const total = rows.length;
  const sumAmount = rows.reduce((s, r) => s + (r.amount ?? 0), 0);
  const showAmount = rows.some((r) => typeof r.amount === "number");

  return (
    <Dialog open={!!open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="p-0 gap-0 w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] sm:max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-hidden rounded-2xl">
        <DialogHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b" style={{ borderColor: BS.cardBorder }}>
          <div className="flex items-center gap-3 pr-8">
            <span
              aria-hidden
              className="h-8 w-1.5 shrink-0 rounded-full"
              style={{ background: open?.tint ?? BS.primary }}
            />
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-sm sm:text-base truncate">{open?.label ?? "Details"}</DialogTitle>
              <p className="text-[11px] mt-0.5" style={{ color: BS.muted }}>
                {loading
                  ? "Loading…"
                  : `${total} ${total === 1 ? "result" : "results"}${showAmount ? ` · €${Math.round(sumAmount).toLocaleString()} total` : ""}`}
              </p>
            </div>
          </div>
        </DialogHeader>
        <div className="max-h-[68vh] sm:max-h-[60vh] overflow-y-auto px-2 py-2">

          {loading ? (
            <div className="grid place-items-center py-10">
              <Loader2 className="size-5 animate-spin" style={{ color: BS.muted }} />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-10 text-center text-sm" style={{ color: BS.muted }}>
              Nothing to show here.
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: BS.cardBorder }}>
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate({ to: "/crm/$clientId", params: { clientId: r.client_id } });
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#f6f7fb] text-left transition-colors"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate" style={{ color: BS.dark }}>
                        {r.primary}
                      </div>
                      <div className="text-[11px] truncate" style={{ color: BS.muted }}>
                        {r.secondary}
                      </div>
                    </div>
                    <ChevronRight className="size-4 shrink-0 opacity-40" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


function MiniStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide" style={{ color: BS.muted }}>{label}</div>
      <div className="text-sm font-mono font-bold mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}

function RepPill({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div
      className="relative overflow-hidden rounded-xl py-2 text-center ring-1 ring-black/[0.04] shadow-[0_1px_2px_rgba(13,17,38,0.06),inset_0_1px_0_rgba(255,255,255,0.9)] transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-[0_6px_16px_-6px_rgba(13,17,38,0.18),inset_0_1px_0_rgba(255,255,255,0.9)]"
      style={{
        background: `linear-gradient(160deg, #ffffff 0%, ${color}10 55%, ${color}1f 100%)`,
        borderTop: `1px solid ${color}26`,
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-xl"
        style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.85), rgba(255,255,255,0))" }}
      />
      <div className="relative text-[9px] font-semibold uppercase tracking-wider" style={{ color: BS.muted }}>{label}</div>
      <div className="relative text-sm font-mono font-bold drop-shadow-[0_1px_0_rgba(255,255,255,0.6)]" style={{ color }}>{value}</div>
    </div>
  );
}


function Kpi({
  icon: Icon,
  label,
  value,
  tint,
  accent,
  onClick,
}: {
  icon: any;
  label: string;
  value: React.ReactNode;
  tint: string;
  accent?: "yellow" | "red";
  onClick?: () => void;
}) {
  const borderColor =
    accent === "red"
      ? "rgba(220,53,69,0.45)"
      : accent === "yellow"
        ? "rgba(245,158,11,0.5)"
        : "rgba(13,17,38,0.08)";
  const interactive = !!onClick;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={`group relative overflow-hidden rounded-2xl border p-2.5 sm:p-4 text-left backdrop-blur-xl shadow-[0_1px_0_rgba(255,255,255,0.6)_inset,0_1px_2px_rgba(13,17,38,0.04),0_10px_28px_-18px_rgba(13,17,38,0.22)] transition-all ${
        interactive
          ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_2px_6px_rgba(13,17,38,0.06),0_18px_36px_-18px_rgba(13,17,38,0.32)] active:scale-[0.985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          : ""
      }`}
      style={{
        borderColor,
        background: `linear-gradient(135deg, ${tint}14 0%, rgba(255,255,255,0.85) 55%, rgba(255,255,255,0.95) 100%)`,
        ...(interactive ? { ["--tw-ring-color" as any]: tint } : {}),
      }}
    >
      {/* glossy top highlight */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px"
        style={{ background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.9), transparent)" }}
      />
      {/* soft color glow */}
      <span
        aria-hidden
        className="pointer-events-none absolute -right-8 -top-8 h-20 w-20 rounded-full blur-2xl"
        style={{ background: `${tint}33` }}
      />
      {/* left accent bar */}
      <span
        aria-hidden
        className="absolute inset-y-0 left-0 w-[3px] rounded-r-full"
        style={{ background: tint, opacity: 0.9 }}
      />
      <div className="relative flex items-start justify-between gap-2">
        <div
          className="grid size-7 sm:size-9 place-items-center rounded-lg sm:rounded-xl ring-1 ring-white/60 shadow-sm"
          style={{ background: `${tint}1f`, color: tint }}
        >
          <Icon className="size-3.5 sm:size-[18px]" strokeWidth={2.2} />
        </div>
        {interactive && (
          <ChevronRight className="size-3.5 sm:size-4 opacity-40 transition-transform group-hover:translate-x-0.5" aria-hidden />
        )}
      </div>
      <div className="relative mt-1.5 sm:mt-2 text-[9px] sm:text-[11px] font-semibold uppercase tracking-[0.06em] truncate" style={{ color: BS.muted }}>
        {label}
      </div>
      <div
        className="relative mt-0.5 text-base sm:text-2xl font-extrabold tracking-tight tabular-nums truncate"
        style={{ color: BS.dark }}
      >
        {value}
      </div>
    </button>
  );
}



function RenewalReminderCronCard() {
  const getHour = useServerFn(getRenewalReminderCronHour);
  const setHour = useServerFn(setRenewalReminderCronHour);
  const q = useQuery({
    queryKey: ["crm", "renewal-cron-hour"],
    queryFn: () => getHour(),
    staleTime: 60_000,
  });
  const current = q.data?.hour ?? null;
  const [value, setValue] = useState<number>(current ?? 8);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (current !== null && current !== undefined) setValue(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const res = await setHour({ data: { hour: value } });
      setMsg(`Saved · runs daily at ${String(res.hour).padStart(2, "0")}:00 UTC`);
      q.refetch();
    } catch (e: any) {
      setMsg(e?.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  const localPreview = (() => {
    try {
      const d = new Date();
      d.setUTCHours(value, 0, 0, 0);
      return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
    } catch {
      return "";
    }
  })();

  return (
    <div className="mt-6">
      <div
        className="rounded-2xl border bg-white p-4 sm:p-5"
        style={{ borderColor: BS.cardBorder }}
      >
        <div className="flex items-start gap-3">
          <Clock className="mt-0.5 size-4 shrink-0" style={{ color: BS.primary }} />
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold" style={{ color: BS.dark }}>
              Renewal reminder cron
            </h3>
            <p className="mt-0.5 text-[11px]" style={{ color: BS.muted }}>
              Sets the UTC hour when the daily <span className="font-mono">crm-renewal-reminders-daily</span> job runs and fires notice-deadline reminders.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[11px] font-medium" style={{ color: BS.muted }}>
              Hour of day (UTC)
            </label>
            <select
              value={value}
              onChange={(e) => setValue(Number(e.target.value))}
              className="mt-1 h-9 rounded-md border bg-white px-2 text-sm"
              style={{ borderColor: BS.cardBorder, color: BS.dark }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {String(i).padStart(2, "0")}:00 UTC
                </option>
              ))}
            </select>
          </div>
          <div className="text-[11px]" style={{ color: BS.muted }}>
            <div>Current: {current === null ? "—" : `${String(current).padStart(2, "0")}:00 UTC`}</div>
            <div>Your local: {localPreview}</div>
          </div>
          <button
            type="button"
            onClick={save}
            disabled={saving || value === current}
            className="ml-auto inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm font-medium text-white disabled:opacity-50"
            style={{ background: BS.primary }}
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            Save
          </button>
        </div>

        {msg && (
          <p className="mt-3 text-[11px]" style={{ color: msg.startsWith("Saved") ? "#198754" : "#dc3545" }}>
            {msg}
          </p>
        )}
      </div>
    </div>
  );
}
